import React from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../context/DarkModeContext';

export const Page: React.FC = () => {
  const { darkMode } = useDarkMode();

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Link 
              to="/" 
              className={`text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300`}
            >
              ← Back to Home
            </Link>
          </div>
          
          <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                At CareerCard, we take your privacy seriously. This Privacy Policy explains how we collect, use, 
                disclose, and safeguard your information when you use our service. Please read this privacy policy 
                carefully. By using CareerCard, you consent to the collection and use of information in accordance 
                with this policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
              <div className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <h3 className="text-xl font-semibold mb-2">2.1 Personal Information</h3>
                <ul className="list-disc pl-6 mb-4">
                  <li>Name and contact information</li>
                  <li>Email address</li>
                  <li>Professional history and experience</li>
                  <li>Resume and career-related documents</li>
                  <li>Account credentials</li>
                </ul>

                <h3 className="text-xl font-semibold mb-2">2.2 Usage Information</h3>
                <ul className="list-disc pl-6 mb-4">
                  <li>Log data and device information</li>
                  <li>Usage patterns and preferences</li>
                  <li>Feature interaction statistics</li>
                  <li>Performance and error data</li>
                </ul>

                <h3 className="text-xl font-semibold mb-2">2.3 AI-Generated Data</h3>
                <ul className="list-disc pl-6">
                  <li>Career insights and recommendations</li>
                  <li>Skills analysis and suggestions</li>
                  <li>Performance metrics and patterns</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We use the collected information for various purposes:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Provide and maintain our service</li>
                <li>Personalize your experience</li>
                <li>Generate AI-powered career insights</li>
                <li>Improve our services and features</li>
                <li>Communicate with you about updates and changes</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Data Storage and Security</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We implement appropriate technical and organizational measures to protect your personal data against 
                unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over 
                the Internet or electronic storage is 100% secure.
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Data encryption in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Secure data backup and recovery procedures</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. Data Sharing and Third Parties</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We may share your information with:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Service providers who assist in operating our service</li>
                <li>Analytics providers to understand service usage</li>
                <li>Law enforcement when required by law</li>
                <li>Business partners with your explicit consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. AI Processing and Data Usage</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Our AI features process your data to provide personalized insights. This includes:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Analysis of career progression patterns</li>
                <li>Skills assessment and recommendations</li>
                <li>Resume optimization suggestions</li>
                <li>Job matching and career path analysis</li>
              </ul>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We may use anonymized data to improve our AI models and services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Your Rights and Choices</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                You have certain rights regarding your personal data:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to data processing</li>
                <li>Export your data</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Cookies and Tracking</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We use cookies and similar tracking technologies to:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Remember your preferences</li>
                <li>Analyze usage patterns</li>
                <li>Maintain secure sessions</li>
                <li>Improve user experience</li>
              </ul>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                You can control cookie preferences through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Our service is not intended for users under the age of 18. We do not knowingly collect personal 
                information from children. If you become aware that a child has provided us with personal information, 
                please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Your information may be transferred to and processed in countries other than your country of residence. 
                We ensure appropriate safeguards are in place to protect your data during such transfers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the 
                new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact Us</h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <ul className={`list-disc pl-6 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <li>Email: privacy@careercard.com</li>
              </ul>
            </section>

            <div className={`mt-8 pt-8 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Last updated: March 2025
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Page; 